import React, { ReactElement, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';

import { Button } from '@/components/atoms/Button';
import { Routes } from '@/core/router/routes.types';
import { useExtendAccountQuery } from '@/services/innovorder/auth/auth.endpoints';

import logo from '../Login/logo.svg';
import { Header, Logo, ProductTitle } from '../Login/Login.style';
import { Body, Panel, Title } from './ExtendAccount.style';

const ExtendAccountPage = (): ReactElement => {
    const history = useHistory();
    const location = useLocation();

    const token = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return params.get('token') ?? '';
    }, [location.search]);

    const { isLoading, isError, isSuccess } = useExtendAccountQuery({ token }, { skip: !token });

    const goToLogin = (): void => history.push(Routes.Login);

    let titleId: string;
    let bodyId: string;
    if (!token) {
        titleId = 'extendAccount.invalidTitle';
        bodyId = 'extendAccount.missingTokenBody';
    } else if (isLoading) {
        titleId = 'extendAccount.title';
        bodyId = 'extendAccount.loadingBody';
    } else if (isSuccess) {
        titleId = 'extendAccount.successTitle';
        bodyId = 'extendAccount.successBody';
    } else {
        // The API returns the same `extend_account_token_invalid` for invalid/expired/unknown
        // user, so the UI surfaces a single rejection state. The dropdown of "what went wrong"
        // is unactionable for the user — they'll need a fresh email if they want to retry.
        titleId = 'extendAccount.invalidTitle';
        bodyId = 'extendAccount.invalidBody';
    }

    const showLoginButton = Boolean(token) && !isLoading;
    const showRetryHint = Boolean(token) && isError;

    return (
        <div>
            <Header>
                <Logo src={logo} />
                <ProductTitle>Backoffice</ProductTitle>
            </Header>
            <Panel>
                <Title>
                    <FormattedMessage id={titleId} />
                </Title>
                <Body>
                    <FormattedMessage id={bodyId} />
                </Body>
                {showRetryHint ? (
                    <Body>
                        <FormattedMessage id="extendAccount.expiredHint" />
                    </Body>
                ) : null}
                {showLoginButton ? (
                    <Button buttonType="primary" fullWidth thick onClick={goToLogin}>
                        <FormattedMessage id="extendAccount.backToLogin" />
                    </Button>
                ) : null}
            </Panel>
        </div>
    );
};

export default ExtendAccountPage;
